import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  MessageSquare,
  Video,
  CreditCard,
  ScrollText,
  User as UserIcon,
  Scale,
} from 'lucide-react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { cn, formatBRL, formatRelativeDate, getInitials } from '@/lib/utils';
import { TASK_TYPE_LABELS, type TaskType, type TaskStatus } from '@parecer/shared';

interface AdminTaskDetailResponse {
  task: {
    taskId: string;
    type: TaskType;
    status: TaskStatus;
    description: string;
    ownerId: string;
    lawyerId?: string;
    totalAmount: number;
    priceAmount: number;
    platformFeeAmount: number;
    deadlineDays?: number;
    createdAt: string;
    updatedAt: string;
    acceptedAt?: string;
    deliveredAt?: string;
    completedAt?: string;
  };
  owner: { userId: string; name: string | null; email: string | null };
  lawyer: { userId: string; name: string | null; email: string | null } | null;
  messages: Array<{
    messageId: string;
    senderId: string;
    senderRole?: string;
    text: string;
    createdAt: string;
    read?: boolean;
  }>;
  calls: Array<{
    callId: string;
    dailyRoomName: string;
    dailyRoomUrl: string;
    scheduledAt: string;
    status: string;
    createdAt: string;
  }>;
  payments: Array<{
    paymentId: string;
    pagarmeOrderId?: string;
    pagarmeChargeId?: string;
    amount: number;
    status: string;
    method: string;
    splitLawyerAmount: number;
    splitPlatformAmount: number;
    escrowReleased: boolean;
    createdAt: string;
  }>;
  auditLog: Array<{
    id: string;
    actorId: string;
    action: string;
    details: Record<string, unknown>;
    createdAt: string;
  }>;
}

export function AdminTarefaDetalhe() {
  const { taskId } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'task', taskId],
    queryFn: () => api.get<AdminTaskDetailResponse>(`/admin/tasks/${taskId}`),
    enabled: Boolean(taskId),
  });

  return (
    <AdminShell>
      <div className="max-w-6xl">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
          <Link to="/admin/tarefas" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Voltar para tarefas
          </Link>
        </Button>

        {isLoading && <p className="text-muted-foreground">Carregando…</p>}
        {error && (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              Não foi possível carregar a tarefa.
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            {/* Header */}
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-xs text-muted-foreground font-mono">{data.task.taskId}</p>
                <h1 className="font-serif text-display-lg mt-1">
                  {TASK_TYPE_LABELS[data.task.type]}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <StatusBadge status={data.task.status} />
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    Criada {formatRelativeDate(data.task.createdAt)}
                  </span>
                  {data.task.totalAmount > 0 && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium">{formatBRL(data.task.totalAmount)}</span>
                    </>
                  )}
                </div>
              </div>

              {data.task.description && (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm whitespace-pre-line text-pretty">{data.task.description}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Parties + financials */}
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <PartyCard
                role="Cliente"
                icon={UserIcon}
                userId={data.owner.userId}
                name={data.owner.name}
                email={data.owner.email}
              />
              {data.lawyer && (
                <PartyCard
                  role="Advogado"
                  icon={Scale}
                  userId={data.lawyer.userId}
                  name={data.lawyer.name}
                  email={data.lawyer.email}
                  link={`/admin/advogados`}
                />
              )}
            </div>

            {/* Financial summary */}
            {data.task.totalAmount > 0 && (
              <Card className="mt-6">
                <CardHeader className="py-4">
                  <CardTitle className="text-base">Valores acordados</CardTitle>
                </CardHeader>
                <CardContent className="grid sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Total cliente</p>
                    <p className="font-serif text-2xl mt-1">{formatBRL(data.task.totalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Plataforma (15%)</p>
                    <p className="font-serif text-2xl mt-1">{formatBRL(data.task.platformFeeAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Advogado recebe</p>
                    <p className="font-serif text-2xl mt-1 text-success">{formatBRL(data.task.priceAmount)}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chat (read-only) */}
            <Card className="mt-6">
              <CardHeader className="py-4 border-b border-border/40">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-4 w-4 text-accent" />
                  Conversa ({data.messages.length})
                </CardTitle>
                <CardDescription>Histórico completo do chat — apenas leitura.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {data.messages.length === 0 ? (
                  <p className="px-6 py-8 text-sm text-muted-foreground italic text-center">
                    Sem mensagens trocadas.
                  </p>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto p-6 space-y-2">
                    {data.messages.map((m, i, arr) => {
                      const fromOwner = m.senderId === data.owner.userId;
                      const senderName = fromOwner
                        ? data.owner.name ?? 'Cliente'
                        : data.lawyer?.name ?? 'Advogado';
                      const prev = i > 0 ? arr[i - 1] : null;
                      const isFirstInRun = !prev || prev.senderId !== m.senderId;
                      return (
                        <div
                          key={m.messageId}
                          className={cn(
                            'flex',
                            fromOwner ? 'justify-start' : 'justify-end',
                            isFirstInRun ? 'mt-3' : 'mt-0.5',
                          )}
                        >
                          {fromOwner && (
                            <div className="w-7 mr-2 flex-none flex items-end">
                              {isFirstInRun && (
                                <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px]">
                                  {getInitials(senderName)}
                                </div>
                              )}
                            </div>
                          )}
                          <div className={cn('max-w-[70%] flex flex-col', fromOwner ? 'items-start' : 'items-end')}>
                            {isFirstInRun && (
                              <span className="text-[11px] text-muted-foreground mb-1 px-1">
                                {senderName}
                              </span>
                            )}
                            <div
                              className={cn(
                                'px-3 py-2 text-sm shadow-sm rounded-2xl',
                                fromOwner
                                  ? 'bg-card border border-border/60'
                                  : 'bg-accent/15 border border-accent/25',
                                isFirstInRun && (fromOwner ? 'rounded-tl-sm' : 'rounded-tr-sm'),
                              )}
                            >
                              <p className="whitespace-pre-wrap break-words">{m.text}</p>
                              <span className="block mt-0.5 text-[10px] text-muted-foreground/80 text-right">
                                {new Date(m.createdAt).toLocaleString('pt-BR', {
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })}
                              </span>
                            </div>
                          </div>
                          {!fromOwner && (
                            <div className="w-7 ml-2 flex-none flex items-end">
                              {isFirstInRun && (
                                <div className="h-7 w-7 rounded-full bg-accent/20 flex items-center justify-center text-[10px]">
                                  {getInitials(senderName)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Calls */}
            <Card className="mt-6">
              <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Video className="h-4 w-4 text-accent" />
                  Calls ({data.calls.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.calls.length === 0 ? (
                  <p className="px-6 pb-6 text-sm text-muted-foreground italic">Nenhuma call agendada.</p>
                ) : (
                  <ul className="divide-y divide-border/40">
                    {data.calls.map((c) => (
                      <li key={c.callId} className="px-6 py-3 flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-medium">
                            {new Date(c.scheduledAt).toLocaleString('pt-BR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {c.dailyRoomName}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="muted">{c.status}</Badge>
                          <a
                            href={c.dailyRoomUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-foreground hover:underline"
                          >
                            Daily ↗
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Payments */}
            <Card className="mt-6">
              <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CreditCard className="h-4 w-4 text-accent" />
                  Pagamentos ({data.payments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.payments.length === 0 ? (
                  <p className="px-6 pb-6 text-sm text-muted-foreground italic">
                    Nenhum pagamento registrado pra esta tarefa.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="border-y border-border/40 text-left text-muted-foreground">
                      <tr>
                        <th className="p-3 font-medium">Charge</th>
                        <th className="p-3 font-medium">Método</th>
                        <th className="p-3 font-medium">Status</th>
                        <th className="p-3 font-medium text-right">Total</th>
                        <th className="p-3 font-medium text-right">Plataforma</th>
                        <th className="p-3 font-medium text-right">Advogado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {data.payments.map((p) => (
                        <tr key={p.paymentId}>
                          <td className="p-3 font-mono text-xs">
                            {p.pagarmeChargeId ?? p.pagarmeOrderId ?? '—'}
                          </td>
                          <td className="p-3 text-xs uppercase">{p.method}</td>
                          <td className="p-3">
                            <Badge variant={paymentVariant(p.status)}>{p.status}</Badge>
                          </td>
                          <td className="p-3 text-right">{formatBRL(p.amount)}</td>
                          <td className="p-3 text-right text-muted-foreground">
                            {formatBRL(p.splitPlatformAmount)}
                          </td>
                          <td className="p-3 text-right text-muted-foreground">
                            {formatBRL(p.splitLawyerAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Audit log */}
            <Card className="mt-6 mb-12">
              <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ScrollText className="h-4 w-4 text-accent" />
                  Audit log ({data.auditLog.length})
                </CardTitle>
                <CardDescription>Ações administrativas que tocaram esta tarefa.</CardDescription>
              </CardHeader>
              <CardContent>
                {data.auditLog.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    Nenhuma ação administrativa registrada.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {data.auditLog.map((a) => (
                      <li key={a.id} className="text-xs">
                        <Badge variant="muted" className="font-mono mr-2">
                          {a.action}
                        </Badge>
                        <span className="text-muted-foreground">
                          por <span className="font-mono">{a.actorId.slice(0, 8)}…</span> ·{' '}
                          {formatRelativeDate(a.createdAt)}
                        </span>
                        <pre className="mt-1 text-[10px] text-muted-foreground/80 whitespace-pre-wrap break-all font-mono">
                          {JSON.stringify(a.details)}
                        </pre>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function PartyCard({
  role,
  icon: Icon,
  name,
  email,
  userId,
}: {
  role: string;
  icon: LucideIcon;
  name: string | null;
  email: string | null;
  userId: string;
  link?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6 flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-medium">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{role}</p>
          <p className="font-medium mt-0.5">{name ?? '—'}</p>
          {email && <p className="text-xs text-muted-foreground">{email}</p>}
          <p className="text-[10px] text-muted-foreground font-mono mt-1">{userId}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { variant: 'success' | 'warning' | 'danger' | 'muted'; label: string }> = {
    open: { variant: 'warning', label: 'Aberta' },
    assigned: { variant: 'warning', label: 'Atribuída' },
    in_progress: { variant: 'warning', label: 'Em andamento' },
    delivered: { variant: 'success', label: 'Entregue' },
    completed: { variant: 'success', label: 'Concluída' },
    rejected: { variant: 'muted', label: 'Rejeitada' },
    cancelled: { variant: 'muted', label: 'Cancelada' },
    disputed: { variant: 'danger', label: 'Em disputa' },
  };
  const info = map[status] ?? { variant: 'muted' as const, label: status };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}

function paymentVariant(s: string): 'default' | 'success' | 'warning' | 'danger' | 'muted' {
  if (s === 'released' || s === 'confirmed') return 'success';
  if (s === 'pending' || s === 'escrowed') return 'warning';
  if (s === 'failed' || s === 'refunded') return 'danger';
  return 'muted';
}
